import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '../../atoms/Button'
import { Icon } from '../../atoms/Icon'
import { NoteAIAssistantProps, RecordingState } from './NoteAIAssistant.types'
import {
  Container,
  RecordButtonContainer,
  InlineRecordButton,
  InfoBanner,
  RecordingStateContainer,
  InlineTimer,
  InlineWaveContainer,
  InlineWaveBar,
  InlineSpinner,
  InlineProcessingText,
  ErrorMessage,
} from './NoteAIAssistant.styles'

const defaultTranslations = {
  recordWithAI: 'Record with AI',
  aiInfo: 'AI will transcribe and format your voice note automatically',
  stopRecording: 'Stop Recording',
  recording: 'Recording in progress...',
  cancel: 'Cancel',
  tryAgain: 'Try Again',
  microphoneError:
    'Unable to access microphone. Please check your permissions.',
  processError: 'Failed to process voice note. Please try again.',
}

export const NoteAIAssistant: React.FC<NoteAIAssistantProps> = ({
  onComplete,
  onCancel,
  onError,
  onReset,
  processingText = 'Processing your recording...',
  analyzingText = 'Analyzing your voice note...',
  errorText = 'Sorry, something went wrong. Please try again.',
  maxRecordingTime = 120,
  isAnalyzing = false,
  translations,
}) => {
  const t = { ...defaultTranslations, ...translations }
  const [recordingState, setRecordingState] = useState<RecordingState>('idle')
  const [recordingTime, setRecordingTime] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const handleStopRecordingRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (recordingState === 'recording') {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= maxRecordingTime - 1) {
            handleStopRecordingRef.current()
            return prev
          }
          return prev + 1
        })
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [recordingState, maxRecordingTime])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop any ongoing recording
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop()
      }

      // Clean up the stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleStartRecording = useCallback(async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      // Start recording
      mediaRecorder.start()
      setRecordingState('recording')
      setRecordingTime(0)
    } catch (error) {
      console.error('Error accessing microphone:', error)
      setRecordingState('error')
      if (onError) {
        onError(t.microphoneError)
      }
    }
  }, [onError, t.microphoneError])

  const handleStopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      setRecordingState('processing')

      // Set up the onstop handler before stopping
      mediaRecorderRef.current.onstop = async () => {
        // Clean up the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }

        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        })

        // Call the onComplete with the audio blob
        // The parent component will handle the API call
        try {
          setRecordingState('analyzing')
          onComplete(audioBlob)
        } catch (error) {
          console.error('Error processing voice note:', error)
          setRecordingState('error')
          if (onError) {
            onError(t.processError)
          }
        }
      }

      // Stop recording
      mediaRecorderRef.current.stop()
    }
  }, [onComplete, onError, t.processError])

  // Update the ref when handleStopRecording changes
  useEffect(() => {
    handleStopRecordingRef.current = handleStopRecording
  }, [handleStopRecording])

  // Reset when parent says we're done analyzing
  useEffect(() => {
    if (!isAnalyzing && recordingState === 'analyzing') {
      setRecordingState('idle')
      setRecordingTime(0)
      audioChunksRef.current = []
      if (onReset) onReset()
    }
  }, [isAnalyzing, recordingState, onReset])

  const handleRetry = useCallback(() => {
    setRecordingState('idle')
    setRecordingTime(0)
  }, [])

  const handleCancel = useCallback(() => {
    // Stop any ongoing recording
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop()
    }

    // Clean up the stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    setRecordingState('idle')
    setRecordingTime(0)
    audioChunksRef.current = []

    if (onCancel) onCancel()
  }, [onCancel])

  const renderContent = () => {
    switch (recordingState) {
      case 'idle':
        return (
          <>
            <RecordButtonContainer>
              <Button onClick={handleStartRecording} type="button" size="small">
                <Icon name="microphone" size="xs" />
                <span>{t.recordWithAI}</span>
              </Button>
            </RecordButtonContainer>
            <InfoBanner>
              <Icon name="sparkles" size="xs" />
              <span>{t.aiInfo}</span>
            </InfoBanner>
          </>
        )

      case 'recording':
        return (
          <>
            <RecordButtonContainer>
              <InlineRecordButton
                $recording
                onClick={handleStopRecording}
                type="button"
              >
                <Icon name="stop" size="small" />
                <span>{t.stopRecording}</span>
              </InlineRecordButton>
            </RecordButtonContainer>
            <RecordingStateContainer>
              <InlineTimer>{formatTime(recordingTime)}</InlineTimer>
              <InlineWaveContainer>
                {Array.from({ length: 5 }).map((_, i) => (
                  <InlineWaveBar key={i} $delay={i * 0.1} />
                ))}
              </InlineWaveContainer>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {t.recording}
              </span>
            </RecordingStateContainer>
          </>
        )

      case 'processing':
        return (
          <RecordingStateContainer>
            <InlineSpinner />
            <InlineProcessingText>{processingText}</InlineProcessingText>
          </RecordingStateContainer>
        )

      case 'analyzing':
        return (
          <RecordingStateContainer>
            <InlineSpinner />
            <InlineProcessingText>{analyzingText}</InlineProcessingText>
          </RecordingStateContainer>
        )

      case 'error':
        return (
          <>
            <ErrorMessage>
              {errorText}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '12px',
                  justifyContent: 'flex-end',
                }}
              >
                <Button variant="secondary" onClick={handleCancel} size="small">
                  {t.cancel}
                </Button>
                <Button variant="primary" onClick={handleRetry} size="small">
                  {t.tryAgain}
                </Button>
              </div>
            </ErrorMessage>
          </>
        )
    }
  }

  return <Container>{renderContent()}</Container>
}
